/**
 * Computes element placements for timeline drag operations.
 *
 * This is a pure, declarative function that computes final element positions
 * given the original state and total drag offset. It handles:
 * - Swapping elements when boundaries are crossed
 * - Multiple swaps for fast drags
 * - Gap preservation (displaced elements shift, gaps remain)
 *
 * Key behavior:
 * - When dragged element passes a neighbor, that neighbor shifts by dragged duration
 * - Neighbors shift from their ORIGINAL position (not intermediate positions)
 * - The dragged element follows the cursor (edge snapping is handled separately)
 * - Gaps are preserved because only passed elements shift
 *
 * @module services/timeline/editing/computeElementPlacements
 */

/**
 * Minimal element data needed for placement computation
 */
export interface TrackElement {
  id: string;
  globalTime: number;
  duration: number;
}

/**
 * Position result for a single element
 */
export interface ElementPosition {
  globalTime: number;
  /** Raw proposed position before snapping (for visual feedback) */
  proposedGlobalTime: number;
}

/**
 * Result of placement computation
 */
export interface PlacementResult {
  /** Map of element ID to new position */
  positions: Map<string, ElementPosition>;
  /** Whether any swap occurred during this computation */
  swapOccurred: boolean;
}

/**
 * Computes new element positions for a drag operation.
 *
 * This is a pure function - it takes original state + offset and returns new state.
 * It does not mutate any input data.
 *
 * @param elements - Array of elements in the track (must be from same track)
 * @param draggedElementId - ID of the element being dragged
 * @param totalOffset - Total time offset from original position (positive = right, negative = left)
 * @returns PlacementResult with new positions and swap indicator
 */
export function computeElementPlacements(
  elements: ReadonlyArray<TrackElement>,
  draggedElementId: string,
  totalOffset: number,
): PlacementResult {
  // Handle edge cases
  if (elements.length === 0) {
    return { positions: new Map(), swapOccurred: false };
  }

  const draggedElement = elements.find(el => el.id === draggedElementId);
  if (!draggedElement) {
    return {
      positions: new Map(
        elements.map(el => [
          el.id,
          { globalTime: el.globalTime, proposedGlobalTime: el.globalTime },
        ]),
      ),
      swapOccurred: false,
    };
  }

  // Sort elements by original time
  const sorted = [...elements].sort((a, b) => a.globalTime - b.globalTime);
  const draggedIndex = sorted.findIndex(el => el.id === draggedElementId);
  const dragged = sorted[draggedIndex];

  // Calculate proposed position (where cursor is)
  const proposedTime = dragged.globalTime + totalOffset;
  const clampedProposedTime = Math.max(0, proposedTime);

  // If no movement, return original positions
  if (totalOffset === 0) {
    return {
      positions: new Map(
        elements.map(el => [
          el.id,
          { globalTime: el.globalTime, proposedGlobalTime: el.globalTime },
        ]),
      ),
      swapOccurred: false,
    };
  }

  // Find target index (how many elements we've passed)
  // Use raw proposedTime (not clamped) so we detect passing even when clamped to 0
  const targetIndex = findTargetIndex(
    sorted,
    draggedIndex,
    dragged,
    proposedTime,
    totalOffset,
  );

  const swapOccurred = targetIndex !== draggedIndex;

  // Dragged element always follows cursor (edge snapping handled separately)
  const draggedGlobalTime = clampedProposedTime;

  // Build positions map
  const positions = computePositions(
    sorted,
    draggedIndex,
    targetIndex,
    dragged,
    draggedGlobalTime,
    proposedTime,
    totalOffset,
  );

  return { positions, swapOccurred };
}

/**
 * Finds the target index (how many elements we've passed).
 *
 * Swap detection criteria (using RAW proposed time, not clamped):
 * - Dragging LEFT: we've passed element[i] when proposedHead <= element[i].head
 * - Dragging RIGHT: we've passed element[i] when proposedTail >= element[i].tail
 *
 * Note: We use <= and >= (not strict < and >) so that snapping exactly to a
 * boundary triggers the swap. This aligns with doesPositionTriggerSwap.
 */
function findTargetIndex(
  sorted: TrackElement[],
  draggedIndex: number,
  dragged: TrackElement,
  proposedTime: number,
  totalOffset: number,
): number {
  let targetIndex = draggedIndex;

  if (totalOffset < 0) {
    // Dragging LEFT - check HEAD boundaries of elements before us
    // Use raw proposedTime (not clamped) so we detect passing even when clamped to 0
    for (let i = draggedIndex - 1; i >= 0; i--) {
      const neighbor = sorted[i];
      const headBoundary = neighbor.globalTime;

      // We've passed this element if our head is at or before their head
      if (proposedTime <= headBoundary) {
        targetIndex = i;
      } else {
        // Haven't reached this element yet
        break;
      }
    }
  } else {
    // Dragging RIGHT - check TAIL boundaries of elements after us
    const proposedEnd = proposedTime + dragged.duration;

    for (let i = draggedIndex + 1; i < sorted.length; i++) {
      const neighbor = sorted[i];
      const tailBoundary = neighbor.globalTime + neighbor.duration;

      // We've passed this element if our tail is at or past their tail
      if (proposedEnd >= tailBoundary) {
        targetIndex = i;
      } else {
        break;
      }
    }
  }

  return targetIndex;
}

/**
 * Computes final positions for all elements.
 *
 * - Dragged element: follows cursor position
 * - Displaced elements: shift by dragged duration from ORIGINAL position
 * - Other elements: stay at original position
 */
function computePositions(
  sorted: TrackElement[],
  draggedIndex: number,
  targetIndex: number,
  dragged: TrackElement,
  draggedGlobalTime: number,
  proposedTime: number,
  totalOffset: number,
): Map<string, ElementPosition> {
  const positions = new Map<string, ElementPosition>();

  for (let i = 0; i < sorted.length; i++) {
    const element = sorted[i];

    if (element.id === dragged.id) {
      // Dragged element
      positions.set(element.id, {
        globalTime: draggedGlobalTime,
        proposedGlobalTime: proposedTime,
      });
    } else {
      // Check if this element was displaced (passed by the dragged element)
      const wasDisplaced =
        totalOffset < 0
          ? i >= targetIndex && i < draggedIndex // Dragging left: elements between target and original
          : i > draggedIndex && i <= targetIndex; // Dragging right: elements between original and target

      if (wasDisplaced) {
        // Shift by dragged duration in opposite direction of drag
        // Dragging left → displaced elements shift right (positive)
        // Dragging right → displaced elements shift left (negative)
        const shift = totalOffset < 0 ? dragged.duration : -dragged.duration;
        const newTime = element.globalTime + shift;

        positions.set(element.id, {
          globalTime: newTime,
          proposedGlobalTime: newTime,
        });
      } else {
        // Not affected - stays at original position
        positions.set(element.id, {
          globalTime: element.globalTime,
          proposedGlobalTime: element.globalTime,
        });
      }
    }
  }

  return positions;
}

/**
 * Checks if a snapped position would trigger a swap with neighbors.
 *
 * This is a lightweight check used after edge snapping to determine if
 * the snapped position crosses a neighbor boundary, requiring a second
 * swap computation pass.
 *
 * @param elements - Array of elements at their FIRST-PASS displaced positions
 * @param draggedElementId - ID of the element being dragged
 * @param snappedGlobalTime - The absolute snapped position (head of dragged element)
 * @param snappedDuration - Duration of the dragged element
 * @returns true if the snapped position triggers a swap
 */
export function doesPositionTriggerSwap(
  elements: ReadonlyArray<TrackElement>,
  draggedElementId: string,
  snappedGlobalTime: number,
  snappedDuration: number,
): boolean {
  // Sort elements by their current positions
  const sorted = [...elements].sort((a, b) => a.globalTime - b.globalTime);
  const draggedIndex = sorted.findIndex(el => el.id === draggedElementId);

  // Element not found - no swap possible
  if (draggedIndex === -1) {
    return false;
  }

  const snappedHead = snappedGlobalTime;
  const snappedTail = snappedGlobalTime + snappedDuration;

  // Check left neighbor (if exists)
  if (draggedIndex > 0) {
    const leftNeighbor = sorted[draggedIndex - 1];
    const leftNeighborHead = leftNeighbor.globalTime;

    // Swap left if snapped head is at or before left neighbor's head
    if (snappedHead <= leftNeighborHead) {
      return true;
    }
  }

  // Check right neighbor (if exists)
  if (draggedIndex < sorted.length - 1) {
    const rightNeighbor = sorted[draggedIndex + 1];
    const rightNeighborTail = rightNeighbor.globalTime + rightNeighbor.duration;

    // Swap right if snapped tail is at or past right neighbor's tail
    if (snappedTail >= rightNeighborTail) {
      return true;
    }
  }

  return false;
}
